/**
 * Partner Hub: a partner team's Discord roster, channel, update reports and
 * server packs, as argus-backend `src/routes/partners.ts` serves them under
 * `/api/notes/partners`.
 *
 * These shapes cross repo boundaries (Argus, Mr. Roboto, Tesseract), so they
 * follow the shared contract field for field. Timestamps are unix
 * milliseconds and Discord snowflakes are strings.
 */

// ── Enums ──────────────────────────────────────────────────────────────────

/** `developer` holds the privileged Partnered Modpack Developer role; tiers are exclusive. */
export type PartnerTier = 'developer' | 'contributor';
export type PartnerPhase = 'active' | 'early' | 'archived' | 'none';
/** Removed rows are tombstones: they are what tells Mr. Roboto to take access away. */
export type PartnerMemberStatus = 'active' | 'removed';
export type PartnerAccessState = 'ok' | 'pending' | 'not_in_server' | 'suspended' | 'held' | 'failed' | 'revoked';
export type PartnerChannelState = 'active' | 'archived' | 'missing' | 'misplaced' | 'none' | 'error';
export type PartnerReportStatus = 'new' | 'acknowledged' | 'scheduled' | 'live' | 'dismissed' | 'superseded';
/** The statuses staff may set. `new` is where reports start; `superseded` is set by the system only. */
export type PartnerReportStaffStatus = 'acknowledged' | 'scheduled' | 'live' | 'dismissed';
export type PartnerReleaseState = 'released' | 'upcoming';
export type PartnerWorldCompat = 'compatible' | 'new_world' | 'unsure';
export type PartnerReportSource = 'partner' | 'staff' | 'curseforge';
export type PartnerChannelOrigin = 'created' | 'linked';
export type PartnerCreatorType = 'individual' | 'team';

export const PARTNER_TIERS: PartnerTier[] = ['developer', 'contributor'];
export const PARTNER_REPORT_STAFF_STATUSES: PartnerReportStaffStatus[] = ['acknowledged', 'scheduled', 'live', 'dismissed'];
export const PARTNER_OPEN_REPORT_STATUSES: PartnerReportStatus[] = ['new', 'acknowledged', 'scheduled'];
export const PARTNER_RELEASE_STATES: PartnerReleaseState[] = ['released', 'upcoming'];
export const PARTNER_WORLD_COMPATS: PartnerWorldCompat[] = ['compatible', 'new_world', 'unsure'];
export const PARTNER_REPORT_SOURCES: PartnerReportSource[] = ['partner', 'staff', 'curseforge'];

// ── Partners ───────────────────────────────────────────────────────────────

/** One row of GET /partners: every creator, with the badges the list pages show. */
export interface PartnerSummary {
  creator_id: string;
  name: string;
  type: PartnerCreatorType;
  phase: PartnerPhase;
  managed: boolean;
  channel: {
    id: string | null;
    state: PartnerChannelState | null;
    observed_at: number | null;
    /** Managed, and Mr. Roboto has not yet reported applying the latest state. */
    pending: boolean;
    error: string | null;
  };
  /** `issues` = active members whose access is neither ok nor pending. */
  members: { developer: number; contributor: number; issues: number };
  open_updates: number;
  latest_update_at: number | null;
}

export interface PartnerDiscord {
  managed: boolean;
  early_access: boolean;
  strict_access: boolean;
  channel_id: string | null;
  channel_origin: PartnerChannelOrigin | null;
  observed_state: PartnerChannelState | null;
  observed_at: number | null;
  observed_detail: { name?: string | null; category_name?: string | null; unmanaged_count?: number } | null;
  /** managed && the bot has not reported the current state hash yet. */
  pending: boolean;
  last_error: string | null;
  /** For "Open in Discord" links; null when Argus has no guild configured. */
  guild_id: string | null;
}

export interface PartnerMember {
  id: string;
  discord_id: string;
  tier: PartnerTier;
  note: string | null;
  status: PartnerMemberStatus;
  discord_username: string | null;
  discord_display_name: string | null;
  discord_avatar_url: string | null;
  in_guild: boolean | null;
  access_state: PartnerAccessState | null;
  access_detail: string | null;
  observed_at: number | null;
  added_at: number;
  added_by: string | null;
  removed_at: number | null;
  removed_by: string | null;
}

export interface PartnerPackCurseForge {
  latest_file_id: number | null;
  latest_display_name: string | null;
  /** Unix ms per the contract's convention; tolerated as an ISO string too. */
  latest_file_date: number | string | null;
  checked_at: number | null;
  last_error: string | null;
}

export interface PartnerWeRunCluster {
  cluster_parent: number | null;
  cluster_name: string | null;
  version: string | null;
  servers: number;
  /** The cluster's head server address, for linking into Modpack Configs. */
  head_address: string | null;
}

export interface PartnerPack {
  modpack_id: string;
  name: string;
  logo_url: string | null;
  platform_url: string | null;
  apogee_id: number | null;
  mpi_id: number | null;
  cf_mod_id: number | null;
  cf: PartnerPackCurseForge | null;
  we_run: PartnerWeRunCluster[];
  latest_report: ReportView | null;
}

export interface PartnerPartnershipRef {
  id: string;
  status: string;
  modpack_id: string | null;
  modpack_name: string | null;
  assigned_to: string | null;
}

export interface PartnerDetail {
  creator: { id: string; name: string; type: PartnerCreatorType; website: string | null };
  phase: PartnerPhase;
  phase_reason: string;
  discord: PartnerDiscord;
  /** Active members first, then removed rows newest first. */
  members: PartnerMember[];
  packs: PartnerPack[];
  partnerships: PartnerPartnershipRef[];
  open_updates: number;
}

export interface PartnerActivity {
  id: string;
  action: string;
  description: string;
  report_id: string | null;
  subject_discord_id: string | null;
  actor_label: string | null;
  created_at: number;
}

export interface PartnerActivityPage {
  items: PartnerActivity[];
  next_before: number | null;
}

// ── Server packs (read from Tesseract) ─────────────────────────────────────

export interface ServerPackFile {
  slug: string;
  filename: string;
  size: number;
  state: string;
  download_count: number;
  max_downloads: number | null;
  created_at: number;
  expires_at: number;
  /** https://craftdownunder.co/f/<slug> */
  download_url: string;
  report_id: string | null;
  context_label: string | null;
  uploaded_by: { discord_id: string; label: string };
}

export interface ServerPackLink {
  grant_id: string;
  context_label: string | null;
  state: string;
  created_at: number;
  expires_at: number;
  requested_by_label: string;
  report_id: string | null;
}

export interface PartnerServerPacks {
  files: ServerPackFile[];
  open_links: ServerPackLink[];
}

// ── Update reports ─────────────────────────────────────────────────────────

/** The one allowlisted report shape, shared by the bot and the user API. */
export interface ReportView {
  id: string;
  creator_id: string;
  creator_name: string;
  modpack_id: string | null;
  pack_name: string;
  version: string;
  version_norm: string;
  release_state: PartnerReleaseState;
  world_compat: PartnerWorldCompat;
  changelog_url: string | null;
  /** Untrusted partner text: always render as plain text. */
  notes: string | null;
  source: PartnerReportSource;
  /** Whether the partner's channel shows this report. */
  partner_visible: boolean;
  /** `discord_id` is null once an erasure has pseudonymised the reporter. */
  reporter: { discord_id: string | null; name: string } | null;
  status: PartnerReportStatus;
  status_note: string | null;
  status_note_public: boolean;
  status_changed_at: number | null;
  status_changed_by_name: string | null;
  curseforge: { file_id: number; display_name: string | null; listed_at: number | null } | null;
  live_at: number | null;
  live_source: 'staff' | 'configs' | null;
  server_pack: { received_at: number; filename: string; size: number } | null;
  superseded_by: string | null;
  revision: number;
  created_at: number;
  updated_at: number;
  argus_url: string;
}

export type PartnerUpdatesStatusFilter = 'open' | 'all' | PartnerReportStatus;

export interface PartnerUpdatesParams {
  status?: PartnerUpdatesStatusFilter;
  creator_id?: string;
  modpack_id?: string;
  source?: PartnerReportSource;
  limit?: number;
  cursor?: string;
}

export interface PartnerUpdatesPage {
  reports: ReportView[];
  next_cursor: string | null;
}

export interface PartnerUpdatesSummary {
  new: number;
  acknowledged: number;
  scheduled: number;
  open: number;
  oldest_new_at: number | null;
}

export interface CreatePartnerReportRequest {
  creator_id: string;
  modpack_id?: string | null;
  pack_name?: string;
  version: string;
  release_state: PartnerReleaseState;
  world_compat?: PartnerWorldCompat;
  changelog_url?: string | null;
  notes?: string | null;
  post_to_partner_channel?: boolean;
}

export interface UpdatePartnerReportRequest {
  status?: PartnerReportStaffStatus;
  status_note?: string | null;
  status_note_public?: boolean;
  modpack_id?: string | null;
}

// ── Mr. Roboto (proxied through Argus) ─────────────────────────────────────

export type PartnerChannelAction = 'create' | 'move' | 'archive' | 'restore' | 'none';

export interface PartnerSyncPlan {
  channel: { action: PartnerChannelAction; category_id: string | null; category_name: string | null; detail: string | null };
  overwrites: { add: string[]; remove: string[] };
  roles: {
    add: { discord_id: string; role: PartnerTier }[];
    remove: { discord_id: string; role: PartnerTier }[];
    held: { discord_id: string; role: PartnerTier; reason: string }[];
  };
}

export interface PartnerObservedMember {
  discord_id: string;
  in_guild: boolean;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  access: PartnerAccessState;
  detail: string | null;
}

/** What the bot applied; the same shape it POSTs to Argus as `observed`. */
export interface PartnerObserved {
  state_hash: string;
  observed_at: number;
  channel: {
    id: string | null;
    created: boolean;
    state: PartnerChannelState;
    name: string | null;
    category_id: string | null;
    category_name: string | null;
    error: string | null;
  };
  members: PartnerObservedMember[];
  unmanaged_count: number;
  events: { action: string; description: string }[];
  errors: unknown[];
}

export interface PartnerUnmanagedPerson {
  discord_id: string;
  username: string | null;
  display_name: string | null;
}

/** The bot's POST /packs/partners/sync answer for one partner. */
export interface PartnerSyncResult {
  creator_id: string;
  applied: boolean;
  dry_run: boolean;
  plan: PartnerSyncPlan;
  /** Absent or null on a dry run, which posts nothing. */
  observed?: PartnerObserved | null;
  /** People with channel access who are not on the team. */
  unmanaged: PartnerUnmanagedPerson[];
  errors: unknown[];
}

/**
 * What happened when Argus poked the bot after a write. The write itself is
 * always saved first; `ok: false` only means Discord has not caught up yet.
 */
export type SyncOutcome =
  | { ok: true; result: PartnerSyncResult }
  | { ok: false; status: number; error: string };

export interface PartnerDiscordRoleHealth {
  id: string | null;
  name: string | null;
  exists: boolean;
  assignable: boolean;
  problems: string[];
  holders: number;
}

export interface PartnerDiscordHealth {
  enabled: boolean;
  remove_roles: boolean;
  guild_id: string | null;
  roles: { developer: PartnerDiscordRoleHealth; contributor: PartnerDiscordRoleHealth };
  categories: {
    id: string;
    name: string | null;
    kind: 'active' | 'archive' | string;
    exists: boolean;
    channels: number;
    room: number;
    missing_permissions: string[];
  }[];
  feed: { id: string | null; name: string | null; ok: boolean; problem: string | null };
  unlinked_channels: { id: string; name: string; category_id: string | null; category_name: string | null }[];
  strays: { discord_id: string; username: string | null; display_name: string | null; roles: PartnerTier[] }[];
  loop: { last_full_at: number | null; last_result: string | null; consecutive_failures: number; snapshot_age_s: number | null };
}

export interface DiscordGuildMember {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  in_guild: boolean;
  is_bot: boolean;
  is_staff: boolean;
  partner_roles: PartnerTier[];
}

// ── Write responses ────────────────────────────────────────────────────────

export interface PartnerDiscordWriteResponse {
  discord: PartnerDiscord;
  sync: SyncOutcome | null;
}

export interface PartnerMemberWriteResponse {
  member: PartnerMember;
  sync: SyncOutcome | null;
}

/** The bot's POST /packs/partners/reports/sync answer: which Discord cards it re-rendered. */
export interface ReportRenderResult {
  rendered: { id: string; partner_message: boolean; feed_message: boolean }[];
  errors: unknown[];
}

/**
 * What a report write says about Discord. The contract names this
 * `SyncOutcome`, but a report change re-renders the report's cards rather
 * than syncing the partner, so argus-backend answers with the bot's
 * reports/sync result here. Null for a partner Discord does not manage.
 */
export type ReportRenderOutcome =
  | { ok: true; result: ReportRenderResult }
  | { ok: false; status: number; error: string };

export interface PartnerReportWriteResponse {
  report: ReportView;
  discord: ReportRenderOutcome | null;
}
